import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  input,
  model,
  viewChild,
} from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';

/**
 * Inline search pill that sits in the sticky top stack under the category tabs. `query` is a
 * model(): it renders the value, and every keystroke (and '' on clear) emits `queryChange` —
 * the parent must keep binding `(queryChange)` to MenuState.setQuery so the fuse index lazy-load
 * still triggers. The parent's `announce` string is mirrored into a live region for readers.
 */
@Component({
  selector: 'app-menu-search',
  imports: [TranslatePipe],
  template: `
      <div class="mx-auto max-w-md px-6 py-3">
        <label class="sr-only" for="menu-search-input">{{ 'menu.searchLabel' | translate }}</label>

        <div class="relative">
          <span
            class="pointer-events-none absolute top-1/2 left-4 -translate-y-1/2 text-slate-400"
            aria-hidden="true"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.3-4.3" />
            </svg>
          </span>

          <input
            #searchInput
            id="menu-search-input"
            type="search"
            [value]="query()"
            (input)="onInput($event)"
            [attr.aria-label]="'menu.searchLabel' | translate"
            [attr.placeholder]="'menu.searchPlaceholder' | translate"
            autocomplete="off"
            enterkeyhint="search"
            class="qm-search block h-11 w-full rounded-full border-0 bg-white pr-11 pl-11 text-base text-slate-900 shadow-sm ring-1 ring-slate-100 ring-inset transition duration-200 placeholder:text-slate-500 focus:ring-2 focus:ring-[var(--brand-fill,var(--brand-primary))] focus:ring-inset focus:outline-none motion-reduce:transition-none [&::-webkit-search-cancel-button]:appearance-none"
          />

          @if (query()) {
            <button
              type="button"
              (click)="clear()"
              [attr.aria-label]="'menu.searchClear' | translate"
              class="z-100 absolute top-1/2 right-2 grid h-8 w-8 -translate-y-1/2 place-items-center rounded-full text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700 focus-visible:ring-2 focus-visible:ring-[var(--brand-fill,var(--brand-primary))] focus-visible:ring-offset-2 focus-visible:outline-none motion-reduce:transition-none"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
              >
                <path d="M18 6 6 18" />
                <path d="m6 6 12 12" />
              </svg>
            </button>
          }
        </div>
        <p class="sr-only" role="status" aria-live="polite">{{ announce() }}</p>
      </div>
  `,
  styles: `
    :host {
      display: block;
      isolation: isolate;
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MenuSearch {
  query = model('');
  announce = input('');

  private inputRef = viewChild<ElementRef<HTMLInputElement>>('searchInput');

  protected onInput(event: Event): void {
    this.query.set((event.target as HTMLInputElement).value);
  }

  protected clear(): void {
    this.query.set('');
    // The clear button unmounts once the query is empty; land focus back on the input so the
    // keyboard stays up and focus never drops to <body>.
    this.inputRef()?.nativeElement.focus();
  }
}
